import { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ClientsService } from "../services/clients";
import { selectUserRoles } from "../../SharedModule/redux/reducers/auth.reducer";
import {
  IsClientDetailInfoLoaded,
  IsClientDetailInfoLoading,
  loadClientDetails,
  SelectClientDetails,
  setIsClientDetailInfoLoading,
} from "../redux/reducers/clientDetails.reducer";
import { addToast } from "../../SharedModule/redux/reducers/ui.reducer";
import { useNavigate } from "react-router";
import {
  ROUTE_CLIENTS,
  ROUTE_FORBIDDEN_PAGE,
} from "../../SharedModule/utils/constants";
import {
  IsAllUsersWithProfileLoaded,
  IsAllUsersWithProfileLoading,
  IsClientsLoaded,
  loadAllUsersWithProfile,
  SelectActualClientForHeaderInfo,
  SelectAllUsersWithProfileInfo,
  SelectClientsIds,
  setAllUsersWithProfileLoading,
} from "../redux/reducers/clientsList.reducer";
import {
  IsBillingInfoLoaded,
  IsBillingInfoLoading,
  loadBillingInfoTab,
  SelectClientBillingInfo,
  setIsBillingInfoLoading,
} from "../redux/reducers/clientBillingInfo.reducer";
import {
  IsLoadingDropdownInfo,
  loadDropdownInfo,
  SelectDropdownsInfo,
  setIsLoadingDropdownInfo,
} from "../redux/reducers/clientDropdownsInfo.reducer";
import { UsersService } from "../../UsersModule/services/users";
import { Client } from "../types/clients-module-types";

import { useClientsList } from "./useClientsList";
import { loadHeaderInfoIfNeeded } from "./clientDataLoaders";

const loadDropdownsIfNeeded = async (
  inFlightRef,
  doneRef,
  dropdownsValues,
  dispatch,
  navigate
) => {
  if (
    inFlightRef.current.dropdowns ||
    doneRef.current.dropdowns ||
    dropdownsValues
  ) {
    return;
  }
  inFlightRef.current.dropdowns = true;
  dispatch(setIsLoadingDropdownInfo(true));
  const dropdownsInfo = await ClientsService.getClientsDropdownsInfo()
    .then((response) => response)
    .catch(() => {
      dispatch(
        addToast({
          mode: "error",
          message:
            "Something wrong happened while getting new client page information.",
        })
      );
      navigate(ROUTE_CLIENTS);
      return true;
    });
  inFlightRef.current.dropdowns = false;
  doneRef.current.dropdowns = true;
  dispatch(loadDropdownInfo(dropdownsInfo.data));
};

const loadUsersIfNeeded = async (
  inFlightRef,
  doneRef,
  isAllUsersWithProfileLoaded,
  dispatch,
  navigate
) => {
  if (
    inFlightRef.current.users ||
    doneRef.current.users ||
    isAllUsersWithProfileLoaded
  ) {
    return;
  }
  inFlightRef.current.users = true;
  dispatch(setAllUsersWithProfileLoading(true));
  const usersWithProfileInfo = await UsersService.getUsersWithProfileInfo()
    .then((response) => response)
    .catch(() => {
      dispatch(
        addToast({
          mode: "error",
          message:
            "Something wrong happened while getting users with profile information.",
        })
      );
      navigate(ROUTE_CLIENTS);
      return true;
    });
  inFlightRef.current.users = false;
  doneRef.current.users = true;
  dispatch(loadAllUsersWithProfile(usersWithProfileInfo));
};

const loadClientDetailsIfNeeded = async (
  inFlightRef,
  doneRef,
  clientId,
  isClientDetailInfoLoaded,
  clientDetails,
  dispatch,
  navigate
) => {
  if (
    inFlightRef.current.details ||
    doneRef.current.details === clientId ||
    (isClientDetailInfoLoaded && clientDetails && clientDetails.id === clientId)
  ) {
    return;
  }
  inFlightRef.current.details = true;
  dispatch(setIsClientDetailInfoLoading(true));
  const clientsInfo = await ClientsService.getClientInfo(clientId)
    .then((data) => {
      if (!data.success) {
        dispatch(
          addToast({
            mode: "error",
            message: "Client not found",
          })
        );
        dispatch(setIsClientDetailInfoLoading(false));
        navigate(ROUTE_CLIENTS);
        return true;
      }
      return data;
    })
    .catch(() => {
      dispatch(
        addToast({
          mode: "error",
          message: "Client not found",
        })
      );
      dispatch(setIsClientDetailInfoLoading(false));
      navigate(ROUTE_CLIENTS);
      return true;
    });
  inFlightRef.current.details = false;
  doneRef.current.details = clientId;
  if (clientsInfo) dispatch(loadClientDetails(clientsInfo));
};

const loadBillingInfoIfNeeded = async (
  inFlightRef,
  doneRef,
  clientId,
  isBillingInfoLoaded,
  clientBillingInfo,
  dispatch,
  navigate
) => {
  if (
    inFlightRef.current.billing ||
    doneRef.current.billing === clientId ||
    (isBillingInfoLoaded &&
      clientBillingInfo &&
      // clientId, not id: id is the billing record's own id, so comparing
      // it to the client id is always true and re-fetches on every render
      clientBillingInfo.clientId === clientId)
  ) {
    return;
  }
  inFlightRef.current.billing = true;
  dispatch(setIsBillingInfoLoading(true));
  const clientsInfo = await ClientsService.getClientBillingInfo(clientId)
    .then((data) => {
      if (!data.success) {
        dispatch(
          addToast({
            mode: "error",
            message: "Client not found",
          })
        );
        dispatch(setIsBillingInfoLoading(false));
        navigate(ROUTE_CLIENTS);
        return true;
      }
      return data;
    })
    .catch(() => {
      dispatch(
        addToast({
          mode: "error",
          message: "Client not found",
        })
      );
      dispatch(setIsBillingInfoLoading(false));
      navigate(ROUTE_CLIENTS);
      return true;
    });
  inFlightRef.current.billing = false;
  doneRef.current.billing = clientId;
  dispatch(loadBillingInfoTab(clientsInfo));
};

export const useClientEdit = (clientId: number) => {
  useClientsList();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { userRolesList, rolesLoaded } = useSelector(selectUserRoles);

  const dropdownsValues = useSelector(SelectDropdownsInfo);
  const isLoadingDropdownInfo = useSelector(IsLoadingDropdownInfo);

  const isAllUsersWithProfileLoaded = useSelector(IsAllUsersWithProfileLoaded);
  const isAllUsersWithProfileLoading = useSelector(
    IsAllUsersWithProfileLoading
  );
  const allUsers = useSelector(SelectAllUsersWithProfileInfo);

  const isClientDetailInfoLoading = useSelector(IsClientDetailInfoLoading);
  const isClientDetailInfoLoaded = useSelector(IsClientDetailInfoLoaded);
  const clientDetails = useSelector(SelectClientDetails);

  const isBillingInfoLoaded = useSelector(IsBillingInfoLoaded);
  const isBillingInfoLoading = useSelector(IsBillingInfoLoading);
  const clientBillingInfo = useSelector(SelectClientBillingInfo);

  // To check is header info is already loaded
  const ClientForHeaderInfo: Client = useSelector(
    SelectActualClientForHeaderInfo
  );
  // to check if clientId is in list of clients that user could see
  const isClientsLoaded = useSelector(IsClientsLoaded);
  const clientsIds = useSelector(SelectClientsIds);

  // Tracks which fetch blocks are in flight, and (in doneRef) which clientId
  // each block last completed for. The callback is async and not awaited by
  // the effect, so each block's dispatch changes a dependency and re-runs the
  // effect while earlier requests are still pending. A second, overlapping
  // invocation is a *different* closure — its own `isXxxLoaded`/`clientDetails`
  // etc. reads are frozen at whatever they were when THAT closure was created,
  // so checking only those against inFlightRef isn't enough: a stale
  // invocation can still see isXxxLoaded as false (its own stale snapshot)
  // right after a newer invocation has already finished and reset the "in
  // flight" flag, and re-fire. doneRef is a plain mutable ref, so every
  // invocation (stale or not) reads its truly-current value.
  const inFlightRef = useRef<Record<string, boolean>>({});
  const doneRef = useRef<Record<string, number | boolean>>({});

  const loadClientToEditInfo = useCallback(
    async (clientId) => {
      if (!rolesLoaded || !isClientsLoaded) {
        return;
      }
      // This check should go in all clients custom hooks
      if (!clientsIds.includes(clientId)) {
        dispatch(
          addToast({
            mode: "error",
            message: "You're not allowed to see this Client Page",
          })
        );
        navigate(ROUTE_FORBIDDEN_PAGE);
        return true;
      }
      // Load info header when user put a new URL
      await loadHeaderInfoIfNeeded(
        inFlightRef,
        doneRef,
        clientId,
        ClientForHeaderInfo,
        dispatch,
        navigate
      );
      // page info
      await loadDropdownsIfNeeded(
        inFlightRef,
        doneRef,
        dropdownsValues,
        dispatch,
        navigate
      );
      // load users for client owner dropdown
      await loadUsersIfNeeded(
        inFlightRef,
        doneRef,
        isAllUsersWithProfileLoaded,
        dispatch,
        navigate
      );
      // client details info
      await loadClientDetailsIfNeeded(
        inFlightRef,
        doneRef,
        clientId,
        isClientDetailInfoLoaded,
        clientDetails,
        dispatch,
        navigate
      );
      // billing info
      await loadBillingInfoIfNeeded(
        inFlightRef,
        doneRef,
        clientId,
        isBillingInfoLoaded,
        clientBillingInfo,
        dispatch,
        navigate
      );
    },
    [dispatch, rolesLoaded, isClientsLoaded, ClientForHeaderInfo, dropdownsValues, isAllUsersWithProfileLoaded, isClientDetailInfoLoaded, clientDetails, isBillingInfoLoaded, clientBillingInfo]
  );

  useEffect(() => {
    loadClientToEditInfo(clientId);
  }, [loadClientToEditInfo]);

  return {
    ClientForHeaderInfo,
    isLoadingDropdownInfo,
    isAllUsersWithProfileLoading,
    dropdownsValues,
    allUsers,
    isClientDetailInfoLoading,
    clientDetails,
    userRolesList,
    rolesLoaded,
    isBillingInfoLoading,
    clientBillingInfo,
    isClientDetailInfoLoaded,
    isBillingInfoLoaded,
  };
};
